import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useSale } from 'root/apps/QuoteMachineIntegration/hooks/useSale';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import CustomerService from 'root/apps/QuoteMachineIntegration/services/CustomerService';
import { AddDepositModalProps } from 'root/apps/QuoteMachineIntegration/state/AddDepositModalProps';
import { AddDepositOptions } from 'root/apps/QuoteMachineIntegration/state/AddDepositOptions';
import { OpenOrdersType } from 'root/apps/QuoteMachineIntegration/state/OpenOrdersType';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { getSaleCreditAccountPayment } from 'root/apps/QuoteMachineIntegration/utils/Sale';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import { datadogRum } from 'root/datadog/datadogRum';

import { OpenOrder } from '../../state/OpenOrdersData';

export const ERROR_MESSAGES = {
  GET_OPEN_ORDERS: 'There was an error fetching open orders for this customer.',
  GET_CUSTOMER_ACCOUNT: 'There was an error fetching the account of this customer.',
};

export function AddDepositModal({
  transactionId,
  onCancel,
  onSubmit,
  onError,
  disableRemoteDeposit,
  disableInPersonDeposit,
}: AddDepositModalProps) {
  const { sale, loading: loadingSale } = useSale(transactionId);
  const [openOrdersCount, setOpenOrdersCount] = useState<number>(0);
  const [openOrdersTotal, setOpenOrdersTotal] = useState<number>(0);
  const [depositBalance, setDepositBalance] = useState<number>(0);
  const [existingDeposit, setExistingDeposit] = useState<number>();
  const {
    data: openOrders,
    isLoading: loadingOpenOrders,
    isError: errorGettingOpenOrders,
  } = useQuery({
    queryKey: ['getOpenOrders', sale],
    queryFn: () =>
      CustomerService.getOpenOrdersOfType(sale?.Customer.customerID ?? '', OpenOrdersType.ALL),
    enabled: !loadingSale,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const {
    data: customerAccount,
    isLoading: loadingCustomerAccount,
    isError: errorGettingCustomerAccount,
  } = useQuery({
    queryKey: ['creditAccountBalance'],
    queryFn: () =>
      CustomerAccountService.getCustomerAccount(`${sale?.Customer.creditAccountID ?? ''}`, ''),
    enabled: !loadingSale,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    window.showNotifier('working');
    window.hideNotifier('warning');
  }, []);

  useEffect(() => {
    if (openOrders?.length) {
      const total = openOrders.reduce((total, order) => total + parseFloat(order.total), 0);
      const allOpenOrderRecord = (openOrders as OpenOrder[]).find(
        (openOrderTotal) => openOrderTotal.type === 'all'
      ) || { type: '', total: '', remaining: '', count: '0' };
      setOpenOrdersCount(Number(allOpenOrderRecord.count));
      setOpenOrdersTotal(total);
    }
  }, [depositBalance, openOrders]);

  useEffect(() => {
    if (!loadingCustomerAccount && customerAccount) {
      setDepositBalance(customerAccount.depositBalance ?? 0);
    }
  }, [customerAccount, loadingCustomerAccount]);

  useEffect(() => {
    if (sale) {
      setExistingDeposit(getSaleCreditAccountPayment(sale));
    }
  }, [sale]);

  function handleCancel() {
    onCancel();
  }

  function handleSubmit(options: AddDepositOptions) {
    onSubmit(options);
  }

  if (errorGettingOpenOrders) {
    window.hideNotifier('working');
    onError(ERROR_MESSAGES.GET_OPEN_ORDERS);
    return <></>;
  }

  if (errorGettingCustomerAccount) {
    window.hideNotifier('working');
    onError(ERROR_MESSAGES.GET_CUSTOMER_ACCOUNT);
    return <></>;
  }

  if (sale && !loadingOpenOrders && !loadingCustomerAccount) {
    window.hideNotifier('working');
    return (
      <Parcel
        key="add-deposit-modal"
        mountParcel={mountRootParcel}
        baseLocation="/register.php"
        skipCSSUnmount={true}
        sessionData={getSessionData(sale)}
        depositBalance={depositBalance}
        openOrdersCount={openOrdersCount}
        openOrdersTotal={openOrdersTotal}
        defaultValue={existingDeposit}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        config={async () =>
          (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
            .AddDepositModalConfig
        }
        handleError={(err: Error) => datadogRum.addError(err)}
        disableRemoteDeposit={disableRemoteDeposit}
        disableInPersonDeposit={disableInPersonDeposit}
      />
    );
  }

  return <></>;
}

export const renderAddDepositModal = (props: AddDepositModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: AddDepositModal,
    selector: '[data-react-root="ReactModal"]',
  });

  return RenderedComponent.mount({
    props: { ...props },
  });
};
